import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Chip } from 'primereact/chip';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Menu } from 'primereact/menu';
import { SelectButton } from 'primereact/selectbutton';
import { Carousel } from 'primereact/carousel';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import ProfileViewHero from '../../components/agentProfile/ProfileViewHero';
import SectionCard from '../../components/agentProfile/SectionCard';
import ExpertiseBadgesList from '../../components/agentProfile/ExpertiseBadgesList';
import MutualConnectionsDialog from '../../components/agentProfile/MutualConnectionsDialog';

import ProfileService from '../../services/agentProfile/ProfileService';
import ConnectionService from '../../services/agentProfile/ConnectionService';
import { socialFeedService } from '../../services/socialFeedService';
import FeedPost from '../../components/social/FeedPost';
import { getLocationLabel } from '../../utils/serviceLocations';
import {
    productLabel,
    roleLabel,
    expertiseLabel,
    skillLabel,
    languageLabel,
} from '../../components/agentProfile/profileCatalogs';

// ─── Helpers (mirrors MyAgentProfile so visuals stay consistent) ────────
const monthShort = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const yearsBetween = (startIso, endIso) => {
    if (!startIso) return '';
    const start = new Date(startIso);
    const end = endIso ? new Date(endIso) : new Date();
    if (Number.isNaN(start.getTime())) return '';
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const yr = Math.floor(months / 12);
    const mo = months % 12;
    if (yr === 0) return `${mo} mo`;
    if (mo === 0) return `${yr} yr${yr > 1 ? 's' : ''}`;
    return `${yr} yr${yr > 1 ? 's' : ''} ${mo} mo`;
};

// ── Activity filter (LinkedIn-style Posts / Comments / Images / Videos) ──
const ACTIVITY_OPTIONS = [
    { label: 'Posts',    value: 'posts'    },
    { label: 'Comments', value: 'comments' },
    { label: 'Images',   value: 'images'   },
    { label: 'Videos',   value: 'videos'   },
];

// Activity carousel: 2 cards side by side on tablet and up, 1 on phones — two
// half-width cards on a 375px screen would leave ~150px per card.
const POST_CAROUSEL_RESPONSIVE = [
    { breakpoint: '768px', numVisible: 1, numScroll: 1 },
];

// A post's media can arrive as media_urls_full / media_urls (strings or
// objects with a `type`/`mime_type`) or a flat media_type on the post — these
// helpers normalise across those shapes so the Images/Videos tabs can filter.
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|avif|bmp)(\?|$)/i;
const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i;
const VIDEO_HOSTS = /youtube\.com|youtu\.be|vimeo\.com/i;

const postMedia = (p) => {
    const m = p.media_urls_full || p.media_urls || p.media || [];
    return Array.isArray(m) ? m : [];
};

const postHasImage = (p) => {
    if ((p.media_type || '') === 'image') return true;
    return postMedia(p).some((m) => {
        if (m && typeof m === 'object') return m.type === 'image' || (m.mime_type || '').startsWith('image/');
        if (typeof m === 'string') return IMAGE_EXT.test(m);
        return false;
    });
};

const postHasVideo = (p) => {
    if ((p.media_type || '') === 'video') return true;
    const hasVideoMedia = postMedia(p).some((m) => {
        if (m && typeof m === 'object') return m.type === 'video' || (m.mime_type || '').startsWith('video/');
        if (typeof m === 'string') return VIDEO_EXT.test(m);
        return false;
    });
    return hasVideoMedia || VIDEO_HOSTS.test(p.content || '');
};

// Compact relative time for comment timestamps ("just now", "5m ago", …).
const timeAgo = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const nameInitials = (name) =>
    (name || 'U').split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

const expiryMeta = (cert) => {
    if (cert.does_not_expire || !cert.expiration_date) {
        return { label: 'No expiry', severity: 'success' };
    }
    const now = new Date();
    const exp = new Date(cert.expiration_date);
    const daysLeft = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: 'Expired', severity: 'danger' };
    if (daysLeft < 90) return { label: `Expires in ${daysLeft}d`, severity: 'warning' };
    return { label: `Valid until ${monthShort(cert.expiration_date)}`, severity: 'success' };
};

// ═════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════
const ViewAgentProfile = () => {
    const { profileId } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);

    const [profile, setProfile] = useState(null);
    const [meta, setMeta] = useState({
        is_own_profile: false,
        connection_status: null,    // null | 'pending' | 'accepted' | 'rejected'
        connection_id: null,
        connection_direction: null, // 'sent' | 'received'
        can_connect: false,
        mutual_count: 0,
        // Server's verdict on whether the Activity tabs are readable. Defaults
        // to false so a private account is never briefly exposed while the
        // profile request is still in flight.
        can_view_activity: false,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [acting, setActing] = useState(false); // any connection action in flight
    const [showAllSkills, setShowAllSkills] = useState(false);
    // The post a card was tapped on — opened full-size in a dialog, since the
    // app has no single-post route to navigate to.
    const [openPostData, setOpenPostData] = useState(null);
    const [showMutualsDialog, setShowMutualsDialog] = useState(false);

    // ── Posts by this profile ──────────────────────────────────────────────
    const [posts, setPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [postsPage, setPostsPage] = useState(1);
    const [postsHasMore, setPostsHasMore] = useState(false);
    const [postsLoadingMore, setPostsLoadingMore] = useState(false);
    // Active Activity tab: posts | comments | images | videos
    const [activityFilter, setActivityFilter] = useState('posts');

    // ── Comments authored by this profile (Comments tab) ──────────────────
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentsLoaded, setCommentsLoaded] = useState(false);
    const [commentsPage, setCommentsPage] = useState(1);
    const [commentsHasMore, setCommentsHasMore] = useState(false);
    const [commentsLoadingMore, setCommentsLoadingMore] = useState(false);

    // ── Load profile ─────────────────────────────────────────────────────
    useEffect(() => {
        if (profileId) loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId]);

    // ── Load this profile's posts once we know its numeric id ──────────────
    // The feed endpoint filters by author_profile_id (profile.id), which the
    // profile payload gives us — not the route :profileId (which may be a slug).
    useEffect(() => {
        if (profile?.id) loadPosts(1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.id]);

    const loadPosts = async (page = 1, append = false) => {
        if (!profile?.id) return;
        // Private account the viewer may not read — the API withholds these
        // anyway. Keyed on the SERVER's verdict, not on is_private_account:
        // a private account's own connections are allowed through, and only
        // the server can see the connection between the two accounts.
        if (!meta.can_view_activity) return;
        if (append) setPostsLoadingMore(true);
        else setPostsLoading(true);
        try {
            const res = await socialFeedService.getFeed({ profile_id: profile.id, per_page: 10, page });
            if (res?.success) {
                const payload = res.data;
                const rows = Array.isArray(payload) ? payload : (payload?.data || []);
                const lastPage = payload?.last_page;
                const curr = payload?.current_page || page;
                setPosts((prev) => (append ? [...prev, ...rows] : rows));
                setPostsPage(curr);
                setPostsHasMore(lastPage ? curr < lastPage : false);
            } else if (!append) {
                setPosts([]);
                setPostsHasMore(false);
            }
        } catch {
            if (!append) { setPosts([]); setPostsHasMore(false); }
        } finally {
            setPostsLoading(false);
            setPostsLoadingMore(false);
        }
    };

    const handlePostUpdate = (updated) =>
        setPosts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    const handlePostDelete = (postId) =>
        setPosts((prev) => prev.filter((p) => p.id !== postId));

    // Lazy-load comments the first time the Comments tab is opened.
    useEffect(() => {
        if (activityFilter !== 'comments') return;
        if (commentsLoaded || commentsLoading) return;
        if (profile?.id) loadComments(1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityFilter, profile?.id, commentsLoaded, commentsLoading]);

    const loadComments = async (page = 1, append = false) => {
        if (!profile?.id) return;
        // Withheld for viewers this private account has not connected with.
        if (!meta.can_view_activity) return;
        if (append) setCommentsLoadingMore(true);
        else setCommentsLoading(true);
        try {
            const res = await socialFeedService.getProfileComments(profile.id, 10, page);
            if (res?.success) {
                const payload = res.data;
                const rows = Array.isArray(payload) ? payload : (payload?.data || []);
                const lastPage = payload?.last_page;
                const curr = payload?.current_page || page;
                setComments((prev) => (append ? [...prev, ...rows] : rows));
                setCommentsPage(curr);
                setCommentsHasMore(lastPage ? curr < lastPage : false);
            } else if (!append) {
                setComments([]);
                setCommentsHasMore(false);
            }
        } catch {
            if (!append) { setComments([]); setCommentsHasMore(false); }
        } finally {
            setCommentsLoading(false);
            setCommentsLoadingMore(false);
            setCommentsLoaded(true);
        }
    };

    const loadProfile = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await ProfileService.getProfileById(profileId);
            if (response.success) {
                setProfile(response.data);
                setMeta({
                    is_own_profile: !!response.meta?.is_own_profile,
                    connection_status: response.meta?.connection_status ?? null,
                    connection_id: response.meta?.connection_id ?? null,
                    connection_direction: response.meta?.connection_direction ?? null,
                    can_connect: !!response.meta?.can_connect,
                    mutual_count: Number(response.meta?.mutual_count) || 0,
                    can_view_activity: !!response.meta?.can_view_activity,
                });

                // If this is my own profile, redirect to MyAgentProfile.
                if (response.meta?.is_own_profile) {
                    navigate('/app/my-agent-profile', { replace: true });
                }
            } else {
                setError(response.message || 'Failed to load profile');
            }
        } catch (err) {
            const status = err.response?.status;
            if (status === 404) setError('Profile not found');
            else if (status === 403) setError('This profile is private');
            else setError(err.response?.data?.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    // ── Connection actions ───────────────────────────────────────────────
    const handleConnect = async () => {
        try {
            setActing(true);
            const response = await ConnectionService.sendRequest({
                receiver_profile_id: profile.id,
                connection_message: `Hi ${profile.full_name}, I'd like to connect with you.`,
            });
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Request sent', life: 2200 });
                await loadProfile();
            } else {
                throw new Error(response.message || 'Failed to send request');
            }
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Connect failed',
                detail: err.response?.data?.message || err.message || 'Try again later',
                life: 3000,
            });
        } finally {
            setActing(false);
        }
    };

    const handleAccept = async () => {
        if (!meta.connection_id) return;
        try {
            setActing(true);
            const response = await ConnectionService.acceptRequest(meta.connection_id);
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Connection accepted', life: 2200 });
                await loadProfile();
            } else {
                throw new Error(response.message || 'Failed to accept');
            }
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Accept failed',
                detail: err.response?.data?.message || err.message,
                life: 3000,
            });
        } finally {
            setActing(false);
        }
    };

    const handleIgnore = () => {
        if (!meta.connection_id) return;
        confirmDialog({
            message: `Ignore ${profile.full_name}'s request? They won't be notified.`,
            header: 'Ignore request',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setActing(true);
                    const response = await ConnectionService.rejectRequest(meta.connection_id);
                    if (response.success) {
                        toast.current?.show({ severity: 'info', summary: 'Request ignored', life: 2000 });
                        await loadProfile();
                    } else {
                        throw new Error(response.message || 'Failed to ignore');
                    }
                } catch (err) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Ignore failed',
                        detail: err.response?.data?.message || err.message,
                        life: 3000,
                    });
                } finally {
                    setActing(false);
                }
            },
        });
    };

    const handleCancel = () => {
        if (!meta.connection_id) return;
        confirmDialog({
            message: `Withdraw your connection request to ${profile.full_name}?`,
            header: 'Withdraw request',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setActing(true);
                    const response = await ConnectionService.removeConnection(meta.connection_id);
                    if (response.success) {
                        toast.current?.show({ severity: 'info', summary: 'Request withdrawn', life: 2000 });
                        await loadProfile();
                    } else {
                        throw new Error(response.message || 'Failed to withdraw');
                    }
                } catch (err) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Withdraw failed',
                        detail: err.response?.data?.message || err.message,
                        life: 3000,
                    });
                } finally {
                    setActing(false);
                }
            },
        });
    };

    const handleRemove = () => {
        if (!meta.connection_id) return;
        confirmDialog({
            message: `Remove ${profile.full_name} from your connections? You can reconnect later.`,
            header: 'Remove connection',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setActing(true);
                    const response = await ConnectionService.removeConnection(meta.connection_id);
                    if (response.success) {
                        toast.current?.show({ severity: 'info', summary: 'Connection removed', life: 2000 });
                        await loadProfile();
                    } else {
                        throw new Error(response.message || 'Failed to remove');
                    }
                } catch (err) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Remove failed',
                        detail: err.response?.data?.message || err.message,
                        life: 3000,
                    });
                } finally {
                    setActing(false);
                }
            },
        });
    };

    const handleMessage = () => {
        navigate(`/app/messaging?profileId=${profile.id}`);
    };

    const copyShareLink = () => {
        navigator.clipboard?.writeText(window.location.href);
        toast.current?.show({ severity: 'info', summary: 'Profile link copied', life: 1500 });
    };

    // ── Activity feed filtering — the selected tab (Posts / Comments / Images /
    // Videos) narrows the fetched post list. Comments has no per-user API yet,
    // so it renders an explanatory empty state.
    const filteredActivity = useMemo(() => {
        if (activityFilter === 'comments') return [];
        if (activityFilter === 'images') return posts.filter(postHasImage);
        if (activityFilter === 'videos') return posts.filter(postHasVideo);
        return posts;
    }, [posts, activityFilter]);

    // Degree badge on each post card ("• 1st"). Only an accepted connection is
    // 1st-degree; anything else leaves the badge off rather than guessing.
    const degreeLabel = meta.connection_status === 'accepted' ? '1st' : null;

    const openPost = (post) => setOpenPostData(post);

    // ── Skills sort: top skills first, then by endorsement count ─────────
    const sortedSkills = useMemo(() => {
        return [...(profile?.skills || [])].sort((a, b) => {
            if (a.is_top_skill && !b.is_top_skill) return -1;
            if (!a.is_top_skill && b.is_top_skill) return 1;
            return (b.endorsement_count || 0) - (a.endorsement_count || 0);
        });
    }, [profile?.skills]);
    const visibleSkills = showAllSkills ? sortedSkills : sortedSkills.slice(0, 10);
    const skillsOverflow = sortedSkills.length - 10;

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — loading
    // ═════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card>
                        <Skeleton width="100%" height="6rem" className="mb-3" />
                        <div className="flex gap-3">
                            <Skeleton shape="circle" size="5.5rem" />
                            <div className="flex-1">
                                <Skeleton width="60%" height="1.5rem" className="mb-2" />
                                <Skeleton width="80%" height="1rem" className="mb-2" />
                                <Skeleton width="40%" height="0.875rem" />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — error
    // ═════════════════════════════════════════════════════════════════════
    if (error || !profile) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card
                        className="shadow-1"
                        pt={{ body: { className: 'p-4 md:p-6' }, content: { className: 'p-0' } }}
                    >
                        <div className="flex flex-column align-items-center text-center gap-3 py-4">
                            <div
                                className="flex align-items-center justify-content-center border-circle"
                                style={{ width: '5rem', height: '5rem', backgroundColor: 'var(--surface-100)' }}
                            >
                                <i
                                    className="pi pi-user-minus text-500"
                                    style={{ fontSize: '2rem' }}
                                    aria-hidden="true"
                                />
                            </div>
                            <h3 className="text-900 font-bold m-0">{error || 'Profile not found'}</h3>
                            <p className="text-600 text-sm m-0">
                                The profile you're looking for might not exist or is set to private.
                            </p>
                            <Button
                                label="Go back"
                                icon="pi pi-arrow-left"
                                outlined
                                severity="secondary"
                                onClick={() => navigate(-1)}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — profile
    // ═════════════════════════════════════════════════════════════════════
    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="grid">
                <div className="col-12">
                    {/* ── 1. HERO (LinkedIn-style, read-only view hero) ─── */}
                    <ProfileViewHero
                        profile={profile}
                        meta={meta}
                        onMutualsClick={meta.mutual_count > 0 ? () => setShowMutualsDialog(true) : undefined}
                        actions={(
                            <ConnectionActions
                                meta={meta}
                                acting={acting}
                                onConnect={handleConnect}
                                onAccept={handleAccept}
                                onIgnore={handleIgnore}
                                onWithdraw={handleCancel}
                                onMessage={handleMessage}
                                onRemove={handleRemove}
                                onShare={copyShareLink}
                            />
                        )}
                    />

                    {/* ── 2. INCOMING REQUEST CALL-OUT ─────────────────── */}
                    {meta.connection_status === 'pending' && meta.connection_direction === 'received' && (
                        <Card
                            className="shadow-1 mb-3 border-left-3 border-blue-500"
                            pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
                        >
                            <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                                <div className="flex align-items-center gap-2 text-700 text-sm">
                                    <i className="pi pi-user-plus text-blue-500" aria-hidden="true" />
                                    <span>
                                        <span className="font-semibold text-900">{profile.full_name}</span>{' '}
                                        sent you a connection request.
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        label="Accept"
                                        icon="pi pi-check"
                                        size="small"
                                        loading={acting}
                                        onClick={handleAccept}
                                    />
                                    <Button
                                        label="Ignore"
                                        size="small"
                                        outlined
                                        severity="secondary"
                                        disabled={acting}
                                        onClick={handleIgnore}
                                    />
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* ── 3. ACTIVITY — Posts / Comments / Images / Videos ── */}
                    <SectionCard
                        icon="pi-bolt"
                        title="Activity"
                        subtitle={`${(profile.connection_count || 0).toLocaleString('en-IN')} ${profile.connection_count === 1 ? 'connection' : 'connections'}`}
                        className="mb-3"
                    >
                        {/* Filter pills — spaced, fully-rounded (SelectButton fuses
                            its buttons by default, so this un-fuses them into gapped
                            pills). Theme-variable colours only. */}
                        <div className="activity-filter mb-3" style={{ overflowX: 'auto' }}>
                            <style>{`
                                .activity-filter .p-selectbutton { display: flex; flex-wrap: wrap; gap: 0.5rem; }
                                .activity-filter .p-selectbutton .p-button {
                                    border-radius: 999px !important;
                                    border: 1px solid var(--surface-border);
                                }
                                .activity-filter .p-selectbutton .p-button.p-highlight {
                                    border-color: var(--primary-color);
                                }
                            `}</style>
                            <SelectButton
                                value={activityFilter}
                                onChange={(e) => e.value && setActivityFilter(e.value)}
                                options={ACTIVITY_OPTIONS}
                                optionLabel="label"
                                optionValue="value"
                                allowEmpty={false}
                                aria-label="Filter activity"
                            />
                        </div>

                        {!meta.can_view_activity && !meta.is_own_profile ? (
                            /* ── Private account the viewer is NOT connected to.
                                  The profile stays visible; only the activity is
                                  withheld. Connections fall through to the tabs. ── */
                            <div className="text-center py-5 text-color-secondary">
                                <div
                                    className="inline-flex align-items-center justify-content-center border-circle bg-orange-100 mb-3"
                                    style={{ width: '3.5rem', height: '3.5rem' }}
                                >
                                    <i className="pi pi-lock text-orange-600 text-2xl" />
                                </div>
                                <p className="m-0 font-medium text-900">This account is private</p>
                                <p className="m-0 mt-1 text-sm">
                                    Their posts and comments are hidden. You can still view their
                                    profile and connect with them.
                                </p>
                            </div>
                        ) : activityFilter === 'comments' ? (
                            /* ── Comments tab — comments authored by this profile ── */
                            commentsLoading ? (
                                <div className="flex flex-column gap-3">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="border-1 surface-border border-round p-3">
                                            <Skeleton width="90%" height="1rem" className="mb-2" />
                                            <Skeleton width="60%" height="0.8rem" />
                                        </div>
                                    ))}
                                </div>
                            ) : comments.length === 0 ? (
                                <div className="text-center py-4 text-color-secondary">
                                    <i className="pi pi-comments text-4xl text-400 mb-2 block" />
                                    <p className="m-0 text-sm">No comments yet.</p>
                                </div>
                            ) : (
                                <div className="flex flex-column gap-3">
                                    {comments.map((c) => (
                                        <div key={c.id} className="surface-ground border-round-lg p-3">
                                            <div className="flex align-items-start gap-3">
                                                <Avatar
                                                    image={c.author?.profile_photo_url}
                                                    label={nameInitials(c.author?.full_name)}
                                                    shape="circle"
                                                    className="flex-shrink-0 cursor-pointer"
                                                    style={{ backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)' }}
                                                    onClick={() => c.author?.id && navigate(`/app/profile/${c.author.id}`)}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex align-items-center gap-2 flex-wrap">
                                                        <span
                                                            className="font-semibold text-900 text-sm cursor-pointer hover:text-primary"
                                                            onClick={() => c.author?.id && navigate(`/app/profile/${c.author.id}`)}
                                                        >
                                                            {c.author?.full_name || 'Someone'}
                                                        </span>
                                                        <span className="text-500 text-xs">{timeAgo(c.created_at)}</span>
                                                    </div>
                                                    <p className="text-900 text-sm m-0 mt-1 line-height-3" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {c.comment_text}
                                                    </p>
                                                    {c.post_excerpt && (
                                                        <div className="surface-card border-round p-2 mt-2 text-600 text-xs" style={{ borderLeft: '3px solid var(--surface-300)' }}>
                                                            <span className="font-medium text-500">On post: </span>
                                                            {c.post_excerpt}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {commentsHasMore && (
                                        <div className="flex justify-content-center">
                                            <Button
                                                label={commentsLoadingMore ? 'Loading...' : 'Load more'}
                                                icon={commentsLoadingMore ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-down'}
                                                className="p-button-outlined p-button-secondary p-button-sm"
                                                onClick={() => loadComments(commentsPage + 1, true)}
                                                disabled={commentsLoadingMore}
                                            />
                                        </div>
                                    )}
                                </div>
                            )
                        ) : postsLoading ? (
                            <div className="flex flex-column gap-3">
                                {[1, 2].map((i) => (
                                    <div key={i} className="border-1 surface-border border-round p-3">
                                        <div className="flex gap-2 align-items-center mb-3">
                                            <Skeleton shape="circle" size="2.5rem" />
                                            <div className="flex-1">
                                                <Skeleton width="40%" height="0.9rem" className="mb-1" />
                                                <Skeleton width="25%" height="0.7rem" />
                                            </div>
                                        </div>
                                        <Skeleton width="100%" height="4rem" />
                                    </div>
                                ))}
                            </div>
                        ) : filteredActivity.length === 0 ? (
                            <div className="text-center py-4 text-color-secondary">
                                <i className={`pi ${activityFilter === 'images' ? 'pi-image' : activityFilter === 'videos' ? 'pi-video' : 'pi-inbox'} text-4xl text-400 mb-2 block`} />
                                <p className="m-0 text-sm">
                                    {activityFilter === 'images'
                                        ? 'No image posts yet.'
                                        : activityFilter === 'videos'
                                            ? 'No video posts yet.'
                                            : 'No posts yet.'}
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-column gap-3">
                                {/* Horizontal carousel of compact cards — 2-up from
                                    md, 1-up on phones. Tapping a card opens the full
                                    post; see ProfilePostCard for why FeedPost isn't
                                    reused inside a narrow slot. */}
                                <Carousel
                                    value={filteredActivity}
                                    numVisible={2}
                                    numScroll={1}
                                    responsiveOptions={POST_CAROUSEL_RESPONSIVE}
                                    showIndicators={false}
                                    itemTemplate={(post) => (
                                        <div className="px-2 h-full">
                                            <ProfilePostCard
                                                post={post}
                                                degreeLabel={degreeLabel}
                                                onOpen={openPost}
                                            />
                                        </div>
                                    )}
                                />
                                {postsHasMore && (
                                    <div className="flex justify-content-center">
                                        <Button
                                            label={postsLoadingMore ? 'Loading...' : 'Load more'}
                                            icon={postsLoadingMore ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-down'}
                                            className="p-button-outlined p-button-secondary p-button-sm"
                                            onClick={() => loadPosts(postsPage + 1, true)}
                                            disabled={postsLoadingMore}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </SectionCard>

                    {/* ── 4. ABOUT ────────────────────────────────────── */}
                    {profile.bio && (
                        <SectionCard icon="pi-info-circle" title="About" className="mb-3">
                            <CollapsibleBio text={profile.bio} />
                        </SectionCard>
                    )}

                    {/* ── 5. FOCUS ────────────────────────────────────── */}
                    {((profile.roles?.length || 0) > 0
                        || (profile.product_expertise?.length || 0) > 0
                        || (profile.expertise?.length || 0) > 0
                        || (profile.service_areas?.length || 0) > 0) && (
                        <SectionCard
                            icon="pi-tag"
                            title="Focus"
                            subtitle="Roles, products, and areas served"
                            className="mb-3"
                        >
                            <ExpertiseBadgesList
                                label="Roles"
                                items={(profile.roles || []).map((r) => roleLabel(r.role_type))}
                                color="blue"
                                showStar
                            />
                            <ExpertiseBadgesList
                                label="Product expertise"
                                items={(profile.product_expertise || []).map((p) => productLabel(p.product_type))}
                                color="indigo"
                            />
                            <ExpertiseBadgesList
                                label="Specializations"
                                items={(profile.expertise || []).map((e) => expertiseLabel(e))}
                                color="teal"
                            />
                            <ExpertiseBadgesList
                                label="Service areas"
                                items={(profile.service_areas || []).map((s) => getLocationLabel(s) || s)}
                                color="purple"
                            />
                            <ExpertiseBadgesList
                                label="Companies I work with"
                                items={profile.associated_companies || []}
                                color="orange"
                            />
                        </SectionCard>
                    )}

                    {/* ── 6. EXPERIENCE ───────────────────────────────── */}
                    {profile.work_experiences?.length > 0 && (
                        <SectionCard icon="pi-briefcase" title="Experience" className="mb-3">
                            <ul className="list-none p-0 m-0">
                                {profile.work_experiences.map((item, idx) => (
                                    <li key={item.id}>
                                        <div className="flex gap-3 py-2">
                                            <Avatar
                                                label={(item.company_name || '??').slice(0, 2).toUpperCase()}
                                                shape="square"
                                                style={{
                                                    width: '2.75rem',
                                                    height: '2.75rem',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 700,
                                                    backgroundColor: 'var(--surface-100)',
                                                    color: 'var(--primary-700)',
                                                    flexShrink: 0,
                                                    borderRadius: '6px',
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <ExperienceContent item={item} />
                                            </div>
                                        </div>
                                        {idx < profile.work_experiences.length - 1 && <Divider className="my-0" />}
                                    </li>
                                ))}
                            </ul>
                        </SectionCard>
                    )}

                    {/* ── 7. EDUCATION ────────────────────────────────── */}
                    {profile.education?.length > 0 && (
                        <SectionCard icon="pi-book" title="Education" className="mb-3">
                            <ul className="list-none p-0 m-0">
                                {profile.education.map((item, idx) => (
                                    <li key={item.id}>
                                        <div className="flex gap-3 py-2">
                                            <Avatar
                                                icon="pi pi-graduation-cap"
                                                shape="square"
                                                style={{
                                                    width: '2.75rem',
                                                    height: '2.75rem',
                                                    backgroundColor: 'var(--surface-100)',
                                                    color: 'var(--primary-700)',
                                                    flexShrink: 0,
                                                    borderRadius: '6px',
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <EducationContent item={item} />
                                            </div>
                                        </div>
                                        {idx < profile.education.length - 1 && <Divider className="my-0" />}
                                    </li>
                                ))}
                            </ul>
                        </SectionCard>
                    )}

                    {/* ── 8. CERTIFICATIONS ───────────────────────────── */}
                    {profile.certifications?.length > 0 && (
                        <SectionCard icon="pi-verified" title="Licenses & certifications" className="mb-3">
                            <div className="grid">
                                {profile.certifications.map((cert) => {
                                    const expiry = expiryMeta(cert);
                                    return (
                                        <div key={cert.id} className="col-12 md:col-6">
                                            <div className="surface-border border-1 border-round p-3 h-full flex flex-column gap-1">
                                                <div className="flex align-items-start justify-content-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-900 font-semibold text-sm line-height-3">
                                                            {cert.certification_name}
                                                        </div>
                                                        <div className="text-700 text-sm">{cert.issuing_organization}</div>
                                                    </div>
                                                    <Tag
                                                        value={expiry.label}
                                                        severity={expiry.severity}
                                                        className="text-xs flex-shrink-0"
                                                        style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }}
                                                    />
                                                </div>
                                                <div className="text-500 text-xs">
                                                    Issued {monthShort(cert.issue_date)}
                                                </div>
                                                {cert.credential_url && (
                                                    <div className="mt-auto pt-2">
                                                        <Button
                                                            label="View credential"
                                                            icon="pi pi-external-link"
                                                            text
                                                            size="small"
                                                            onClick={() => window.open(cert.credential_url, '_blank', 'noopener')}
                                                            className="p-0"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </SectionCard>
                    )}

                    {/* ── 9. SKILLS ───────────────────────────────────── */}
                    {sortedSkills.length > 0 && (
                        <SectionCard
                            icon="pi-star"
                            title="Skills"
                            subtitle={`${sortedSkills.length} ${sortedSkills.length === 1 ? 'skill' : 'skills'}`}
                            className="mb-3"
                        >
                            <div className="flex flex-wrap gap-2">
                                {visibleSkills.map((s) => (
                                    <div
                                        key={s.id}
                                        className="border-round px-2 py-1 flex align-items-center gap-2"
                                        style={{
                                            backgroundColor: s.is_top_skill ? 'var(--primary-color)' : 'var(--blue-50)',
                                            color: s.is_top_skill ? 'var(--primary-color-text)' : 'var(--blue-900)',
                                            border: s.is_top_skill ? 'none' : '1px solid var(--blue-200)',
                                        }}
                                    >
                                        {s.is_top_skill && (
                                            <i
                                                className="pi pi-star-fill"
                                                style={{ fontSize: '0.65rem' }}
                                                aria-hidden="true"
                                            />
                                        )}
                                        <span className="text-sm font-medium">{skillLabel(s.skill_name)}</span>
                                        {s.endorsement_count > 0 && (
                                            <span
                                                className="text-xs flex align-items-center gap-1"
                                                style={{ opacity: 0.85 }}
                                            >
                                                <i
                                                    className="pi pi-thumbs-up-fill"
                                                    style={{ fontSize: '0.6rem' }}
                                                    aria-hidden="true"
                                                />
                                                {s.endorsement_count}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {skillsOverflow > 0 && (
                                <div className="mt-2">
                                    <Button
                                        label={showAllSkills ? 'Show less' : `Show all ${sortedSkills.length} skills`}
                                        text
                                        size="small"
                                        onClick={() => setShowAllSkills(!showAllSkills)}
                                    />
                                </div>
                            )}
                        </SectionCard>
                    )}

                    {/* ── 10. LANGUAGES ───────────────────────────────── */}
                    {profile.languages?.length > 0 && (
                        <SectionCard icon="pi-globe" title="Languages" className="mb-3">
                            <div className="flex flex-wrap gap-2">
                                {profile.languages.map((l) => (
                                    <Chip
                                        key={l.id}
                                        label={languageLabel(l.language_name)}
                                        icon="pi pi-globe"
                                        className="text-sm"
                                    />
                                ))}
                            </div>
                        </SectionCard>
                    )}
                </div>
            </div>

            {/* ── Mutual connections dialog (Instagram-style list) ─────── */}
            <MutualConnectionsDialog
                visible={showMutualsDialog}
                onHide={() => setShowMutualsDialog(false)}
                profileId={profile.id}
                profileName={profile.full_name}
                onSelectProfile={(id) => navigate(`/app/profile/${id}`)}
            />

            {/* ── Full post, opened from an activity card ───────────────
                The carousel card is a summary; this is where the real
                <FeedPost> lives, at full width where it was designed to
                run — reactions, comments and reshare all work here. */}
            <Dialog
                visible={!!openPostData}
                onHide={() => setOpenPostData(null)}
                header="Post"
                modal
                draggable={false}
                dismissableMask
                style={{ width: '90vw', maxWidth: '600px' }}
            >
                {openPostData && (
                    <FeedPost
                        post={openPostData}
                        onUpdate={(updated) => {
                            handlePostUpdate(updated);
                            setOpenPostData((cur) => (cur && cur.id === updated?.id ? { ...cur, ...updated } : cur));
                        }}
                        onDelete={(id) => { handlePostDelete(id); setOpenPostData(null); }}
                    />
                )}
            </Dialog>
        </>
    );
};

// ═════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════════

/**
 * Connection action row in the hero — Instagram / LinkedIn-style.
 * Every primary action is a VISIBLE button, never buried in a kebab menu:
 *
 *   • can_connect / no status              → [Connect] [Share]
 *   • pending + sent by me                 → [Pending — disabled] [Withdraw] [Share]
 *   • pending + received from them         → [Accept] [Ignore] [Share]
 *   • accepted                             → [Message] [Remove] [Share]
 *
 * The Message button always uses pi-envelope. Remove always uses pi-user-minus.
 * Both stay visible so the user never needs to hunt through a menu to
 * disconnect — same pattern as Instagram's "Following" button and LinkedIn's
 * "Connected" / "Message" pair.
 */
const ConnectionActions = ({ meta, acting, onConnect, onAccept, onIgnore, onWithdraw, onMessage, onRemove, onShare }) => {
    const { connection_status: status, connection_direction: dir, can_connect } = meta;
    const moreMenuRef = useRef(null);

    // "More" menu shown next to Message once connected — Share + Remove live
    // here so the primary action (Message) stays front-and-centre.
    const moreItems = [
        { label: 'Share profile', icon: 'pi pi-share-alt', command: onShare },
        { separator: true },
        { label: 'Remove connection', icon: 'pi pi-user-minus', command: onRemove },
    ];

    const ShareButton = (
        <Button
            icon="pi pi-share-alt"
            rounded
            text
            size="small"
            tooltip="Share profile"
            tooltipOptions={{ position: 'bottom' }}
            onClick={onShare}
            aria-label="Share profile"
        />
    );

    // Pending request received — Accept (primary) + Ignore (visible).
    if (status === 'pending' && dir === 'received') {
        return (
            <>
                <Button
                    label="Accept"
                    icon="pi pi-check"
                    size="small"
                    loading={acting}
                    onClick={onAccept}
                />
                <Button
                    label="Ignore"
                    icon="pi pi-times"
                    size="small"
                    outlined
                    severity="secondary"
                    disabled={acting}
                    onClick={onIgnore}
                />
                {ShareButton}
            </>
        );
    }

    // Pending sent by me — Pending status (disabled) + Withdraw (visible).
    if (status === 'pending' && dir === 'sent') {
        return (
            <>
                <Button
                    label="Pending"
                    icon="pi pi-clock"
                    size="small"
                    severity="secondary"
                    outlined
                    disabled
                    tooltip="Waiting for response"
                    tooltipOptions={{ position: 'bottom' }}
                />
                <Button
                    label="Withdraw"
                    icon="pi pi-times"
                    size="small"
                    outlined
                    severity="danger"
                    disabled={acting}
                    onClick={onWithdraw}
                />
                {ShareButton}
            </>
        );
    }

    // Accepted — primary "Message" button + a "•••" more menu (Share / Remove).
    // Matches LinkedIn: once connected the Message action is front-and-centre,
    // and the less-common disconnect/share options tuck into the overflow menu.
    if (status === 'accepted') {
        return (
            <>
                <Button
                    label="Message"
                    icon="pi pi-send"
                    size="small"
                    onClick={onMessage}
                    aria-label="Message"
                />
                <Menu
                    model={moreItems}
                    popup
                    ref={moreMenuRef}
                    appendTo={typeof document !== 'undefined' ? document.body : undefined}
                />
                <Button
                    label="More actions"
                    icon="pi pi-chevron-down"
                    iconPos="right"
                    outlined
                    size="small"
                    severity="secondary"
                    disabled={acting}
                    onClick={(e) => moreMenuRef.current?.toggle(e)}
                    aria-label="More actions"
                    aria-haspopup
                />
            </>
        );
    }

    // Default — no connection: Connect (primary) + Share.
    return (
        <>
            <Button
                label="Connect"
                icon="pi pi-user-plus"
                size="small"
                loading={acting}
                disabled={!can_connect || acting}
                onClick={onConnect}
            />
            {ShareButton}
        </>
    );
};

// ── Profile Activity post card ────────────────────────────────────────────
// A COMPACT read-only card, deliberately not the full <FeedPost>. FeedPost is
// built for a full-width feed column — inside a half-width carousel slot its
// media, comment composer and expanded action bar wrap badly. This shows only
// what identifies a post at a glance; tapping it opens the real thing.

/** Compact relative time — "1d", "5d", "3h". The feed's formatTimeAgo returns
 *  "1 day ago", which is too long for a card header that also carries the
 *  edited flag and the visibility icon. */
const formatTimeCompact = (dateString) => {
    if (!dateString) return '';
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (!Number.isFinite(seconds) || seconds < 60) return 'now';
    const units = [['y', 31536000], ['mo', 2592000], ['w', 604800], ['d', 86400], ['h', 3600], ['m', 60]];
    for (const [suffix, secs] of units) {
        const n = Math.floor(seconds / secs);
        if (n >= 1) return `${n}${suffix}`;
    }
    return 'now';
};

/** Visibility → PrimeIcon. Mirrors the feed's own vocabulary. */
const visibilityIcon = (visibility) => {
    if (visibility === 'connections') return 'pi-users';
    if (visibility === 'private' || visibility === 'only_me') return 'pi-lock';
    return 'pi-globe';
};

/** `link_preview` is a free-form JSON column with no writer in the frontend
 *  yet, so the key names aren't guaranteed. Accept the common spellings and
 *  derive the domain from the URL when it isn't supplied. */
const normalizeLinkPreview = (lp) => {
    if (!lp || typeof lp !== 'object') return null;
    const url = lp.url || lp.link || '';
    const title = lp.title || lp.og_title || '';
    const image = lp.image || lp.og_image || lp.thumbnail || lp.image_url || '';
    if (!url && !title) return null;
    let domain = lp.domain || lp.site_name || '';
    if (!domain && url) {
        try { domain = new URL(url).hostname.replace(/^www\./, ''); } catch { domain = ''; }
    }
    return { url, title, image, domain };
};

/** First image in the post, used as the preview thumbnail when the post has no
 *  link card. Media arrives as plain strings or as objects with a `url`. */
const firstPostImage = (post) => {
    const list = post.media_urls_full || post.media_urls || [];
    if (!Array.isArray(list) || list.length === 0) return '';
    const item = list[0];
    const url = typeof item === 'string' ? item : (item?.url || '');
    const type = typeof item === 'object' ? (item.type || item.mime_type || '') : '';
    if (type && !String(type).startsWith('image')) return '';
    return url;
};

/** Action-bar entry. Count is hidden at zero — the reference shows a bare icon
 *  until there is something to report, which keeps a quiet post quiet. */
const PostStat = ({ icon, count, label }) => (
    <span className="inline-flex align-items-center gap-1 text-600" aria-label={label}>
        <i className={`pi ${icon} text-sm`} aria-hidden="true" />
        {count > 0 && <span className="text-xs">{count}</span>}
    </span>
);

const ProfilePostCard = ({ post, degreeLabel, onOpen }) => {
    const author = post.author_profile || {};
    const preview = normalizeLinkPreview(post.link_preview);
    const thumb = preview?.image || (preview ? '' : firstPostImage(post));
    const edited = !!post.edited_at;

    return (
        <div
            className="surface-card border-1 surface-border border-round p-3 h-full flex flex-column cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => onOpen(post)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(post); } }}
            aria-label={`Open post by ${author.full_name || 'this member'}`}
        >
            {/* Header — avatar, name + degree, headline, time */}
            <div className="flex align-items-start gap-2 mb-2">
                {author.profile_photo_url_full ? (
                    <img
                        src={author.profile_photo_url_full}
                        alt=""
                        className="border-circle flex-shrink-0"
                        style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover' }}
                    />
                ) : (
                    <Avatar
                        label={(author.full_name || 'U').trim().charAt(0).toUpperCase()}
                        shape="circle"
                        className="bg-primary flex-shrink-0"
                        style={{ width: '2.5rem', height: '2.5rem' }}
                    />
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex align-items-center gap-1">
                        <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {author.full_name || 'Member'}
                        </span>
                        {degreeLabel && <span className="text-500 text-xs flex-shrink-0">• {degreeLabel}</span>}
                    </div>
                    {author.headline && (
                        <div className="text-600 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {author.headline}
                        </div>
                    )}
                    <div className="flex align-items-center gap-1 text-500 text-xs mt-1">
                        <span>{formatTimeCompact(post.created_at)}</span>
                        {edited && <><span>•</span><span>Edited</span></>}
                        <span>•</span>
                        <i className={`pi ${visibilityIcon(post.visibility)} text-xs`} aria-hidden="true" />
                    </div>
                </div>
            </div>

            {/* Body — clamped to 2 lines with a "more" affordance. No PrimeFlex
                utility exists for line-clamp, so this is the one inline style. */}
            {post.content && (
                <>
                    <p
                        className="text-900 text-sm line-height-3 m-0"
                        style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {post.content}
                    </p>
                    <span className="text-500 text-sm">… more</span>
                </>
            )}

            {/* Link card, else a plain media thumbnail */}
            {preview ? (
                <div className="flex surface-border border-1 border-round overflow-hidden mt-2">
                    {preview.image && (
                        <img
                            src={preview.image}
                            alt=""
                            className="flex-shrink-0"
                            style={{ width: '6.5rem', height: '5rem', objectFit: 'cover' }}
                        />
                    )}
                    <div className="p-2 flex-1 min-w-0 flex flex-column justify-content-center">
                        <span
                            className="text-900 font-semibold text-sm line-height-3"
                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        >
                            {preview.title || preview.url}
                        </span>
                        {preview.domain && <span className="text-500 text-xs mt-1">{preview.domain}</span>}
                    </div>
                </div>
            ) : thumb ? (
                <img
                    src={thumb}
                    alt=""
                    className="border-round mt-2 w-full"
                    style={{ height: '8rem', objectFit: 'cover' }}
                />
            ) : null}

            {/* Action bar pinned to the bottom so cards of different content
                length still line their footers up across the carousel. */}
            <div className="flex align-items-center gap-4 pt-3 mt-auto">
                <PostStat icon="pi-thumbs-up" count={post.like_count || 0} label="Reactions" />
                <PostStat icon="pi-comment" count={post.comment_count || 0} label="Comments" />
                <PostStat icon="pi-replay" count={post.share_count || 0} label="Reposts" />
                <PostStat icon="pi-send" count={0} label="Send" />
            </div>
        </div>
    );
};

const ExperienceContent = ({ item }) => (
    <div>
        <div className="flex align-items-center gap-2 flex-wrap">
            <span className="text-900 font-semibold text-sm">{item.job_title}</span>
            {item.is_current && (
                <Tag value="Current" severity="success" className="text-xs" style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem' }} />
            )}
        </div>
        <div className="text-700 text-sm">{item.company_name}</div>
        <div className="text-500 text-xs mt-1">
            {monthShort(item.start_date)} — {item.is_current ? 'Present' : monthShort(item.end_date)}
            {' · '}
            {yearsBetween(item.start_date, item.is_current ? null : item.end_date)}
            {item.location ? ` · ${item.location}` : ''}
        </div>
        {item.description && (
            <p className="text-600 text-xs md:text-sm mt-2 mb-0 line-height-3">{item.description}</p>
        )}
    </div>
);

const EducationContent = ({ item }) => (
    <div>
        <div className="flex align-items-center gap-2 flex-wrap">
            <span className="text-900 font-semibold text-sm">
                {item.degree_type ? item.degree_type.replace(/_/g, ' ').toUpperCase() : ''}
                {item.field_of_study ? ` in ${item.field_of_study}` : ''}
            </span>
            {item.is_ongoing && (
                <Tag value="Ongoing" severity="info" className="text-xs" style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem' }} />
            )}
        </div>
        <div className="text-700 text-sm">{item.institution_name}</div>
        <div className="text-500 text-xs mt-1">
            {monthShort(item.start_date)} — {item.is_ongoing ? 'Present' : monthShort(item.end_date)}
            {item.grade ? ` · Grade: ${item.grade}` : ''}
        </div>
    </div>
);

const CollapsibleBio = ({ text }) => {
    const [expanded, setExpanded] = useState(false);
    const isLong = text.length > 240;
    const displayText = expanded || !isLong ? text : `${text.slice(0, 240)}…`;
    return (
        <div>
            <p className="text-700 text-xs md:text-sm line-height-3 m-0 white-space-pre-line">
                {displayText}
            </p>
            {isLong && (
                <Button
                    label={expanded ? 'Show less' : 'Show more'}
                    text
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    className="p-0 mt-1"
                />
            )}
        </div>
    );
};

export default ViewAgentProfile;
